import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

/**
 * Route guard for authenticated screens.
 *
 * History (bug #4, reported 2026-07-31): this component accepted a
 * `requiredRole` prop and checked it — but no call site in either SPA ever
 * passed one, so in practice it gated on "is signed in" and nothing else. A
 * tenant who reached an admin URL got the admin page, which then rendered the
 * API's 403 as an in-page "⚠️ Admin privileges required" banner. The role was
 * enforced by the backend (correctly) and by the frontend (not at all).
 *
 * The old failure target was `/unauthorized`, a path registered in neither
 * SPA, so even a correct call site would have fallen through to a catch-all.
 *
 * Redirect targets are deliberately different in kind:
 *
 *  - **Not signed in** → `<Navigate to="/login">`, a client-side navigation
 *    within the current bundle. Under the admin bundle's `/admin` basename
 *    that resolves to `/admin/login`; under the tenant bundle, `/login`.
 *
 *  - **Signed in, wrong role** → a FULL PAGE navigation to the other plane.
 *    The admin and tenant SPAs are separate bundles served from different
 *    mount prefixes, so react-router cannot navigate between them; `<Navigate>`
 *    would rewrite the URL while leaving the wrong bundle running. This is the
 *    same cross-bundle mistake that broke "Login as" (bug #1).
 */

interface ProtectedRouteProps {
  children: React.ReactNode
  /**
   * Role required to view the route. Omit for screens that only require a
   * signed-in user (the whole tenant plane — admins view it while
   * impersonating).
   */
  requiredRole?: 'admin'
  /**
   * Render even while the account must change its password. Only the
   * change-password page sets this; every other protected screen redirects
   * there until the password is changed.
   */
  allowPendingPasswordChange?: boolean
}

/** Where a signed-in user with the wrong role belongs instead. */
const TENANT_HOME = '/client'

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  allowPendingPasswordChange = false,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth()

  const roleMismatch =
    !isLoading &&
    isAuthenticated &&
    requiredRole === 'admin' &&
    user?.role !== 'admin'

  React.useEffect(() => {
    if (roleMismatch) {
      // replace() so the back button doesn't bounce the user straight back
      // into a screen they aren't allowed to see.
      window.location.replace(TENANT_HOME)
    }
  }, [roleMismatch])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ returnTo: window.location.pathname + window.location.search + window.location.hash }} />
  }

  if (user?.must_change_password && !allowPendingPasswordChange) {
    return <Navigate to="/change-password" replace state={{ returnTo: window.location.pathname }} />
  }

  if (roleMismatch) {
    // The effect above is navigating away. Render an explicit interstitial
    // rather than the protected children — a flash of admin content is a
    // real (if brief) information leak, and the testid gives the cross-role
    // regression test something deterministic to assert on.
    return (
      <div
        data-testid="role-redirect"
        className="flex items-center justify-center min-h-screen"
      >
        <p className="text-gray-600 dark:text-gray-400">
          Redirecting to your account…
        </p>
      </div>
    )
  }

  return <>{children}</>
}
